import { useTexture, shaderMaterial } from "@react-three/drei";
import { Canvas, extend } from "@react-three/fiber";
import { createDriftpane } from "@niccolofanton/driftpane";
import "@niccolofanton/driftpane/theme.css";
import { Pane } from "tweakpane";
import { Suspense, useEffect, useMemo, useState } from "react";
import * as THREE from "three";

import materialFragmentShader from "!!raw-loader!../shaders/materialFragmentShader.glsl";

const ASCII_CHARS = "QWERTYUIOPASDFGHJKLZXCVBNM↑↓→←~!@#$%^&*()_+;-={}[]:;<>?/";
const DEFAULT_SETTINGS = {
  asciiChars: ASCII_CHARS,
  pixelSize: 40,
  background: "#ffffff",
};

const PRESETS = {
  Keyboard: DEFAULT_SETTINGS,
  Sparse: { asciiChars: "QWERTYUIOPASDFGHJKLZXCVBNM", pixelSize: 72, background: "#ffffff" },
  Dense: { asciiChars: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789", pixelSize: 24, background: "#ffffff" },
  Symbols: { asciiChars: "↑↓→←~!@#$%^&*()_+;-={}[]:;<>?/", pixelSize: 40, background: "#ffffff" },
};

const ColorShiftMaterial = shaderMaterial(
  {
    u_texture: { value: null },
    pixelSize: { value: 40 },
    asciiTexture: { value: null },
    charCount: { value: null },
    charSize: { value: null },
    imageAspect: { value: 1 },
  },
  // vertex shader
  /*glsl*/ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  // fragment shader
  materialFragmentShader
);

extend({ ColorShiftMaterial });

const FullScreenImage = ({ settings }) => {
  const fontFamily = "Azeret Mono";
  const texture = useTexture(
    "https://cdn.maximeheckel.com/images/backgrounds/gril-with-pearl-earing.jpg"
  );

  const material = useMemo(
    () => new ColorShiftMaterial({ side: THREE.DoubleSide }),
    []
  );

  useEffect(() => {
    const charSize = settings.pixelSize;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) return undefined;

    canvas.width = charSize * settings.asciiChars.length;
    canvas.height = charSize;

    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "white";
    ctx.font = `${charSize / 1.95}px ${fontFamily}`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";

    settings.asciiChars.split("").forEach((char, i) => {
      ctx.fillText(char, (i + 0.5) * charSize, charSize / 2);
    });

    const asciiTexture = new THREE.CanvasTexture(canvas);
    asciiTexture.minFilter = THREE.NearestFilter;
    asciiTexture.magFilter = THREE.NearestFilter;

    material.asciiTexture = asciiTexture;
    material.charCount = [settings.asciiChars.length, 1];
    material.charSize = charSize;
    material.pixelSize = settings.pixelSize;
    material.imageAspect = texture.image.width / texture.image.height;

    return () => asciiTexture.dispose();
  }, [material, settings, texture]);

  useEffect(() => {
    material.u_texture = texture;
  }, [material, texture]);

  return (
    <>
      <mesh material={material} position={[0, 0.2, 0]} scale={0.58}>
        <planeGeometry
          args={[texture.image.width / 300, texture.image.height / 300]}
        />
        {/* <meshStandardMaterial map={texture} /> */}
      </mesh>

      {/* <Image
        ref={meshRef}
        side={THREE.DoubleSide}
        scale={[viewport.width / 2, viewport.height / 2, 1.0]}
        url="https://cdn.maximeheckel.com/images/backgrounds/gril-with-pearl-earing.jpg"
      /> */}
    </>
  );
};

const Scene = ({ settings }) => {
  return (
    <>
      <Canvas
        shadows
        dpr={[1, 1.5]}
        style={{ background: settings.background }}
      >
        <Suspense>
          {/* <color attach="background" args={["#74B7FF"]} /> */}
          <ambientLight intensity={0.8} />
          <directionalLight position={[5, 10, -5]} intensity={10.0} />

          {/* <OrthographicCamera
            makeDefault
            position={[0, 0, 10]}
            zoom={100}
            near={0.01}
            far={500}
          /> */}

          <FullScreenImage settings={settings} />
        </Suspense>
      </Canvas>
    </>
  );
};

const Controls = ({ onChange, onReady }) => {
  useEffect(() => {
    const settings = { ...DEFAULT_SETTINGS };
    const pane = new Pane({ title: "Controls" });

    const atlas = pane.addFolder({ title: "Character atlas" });
    atlas.addBinding(settings, "pixelSize", {
      label: "Cell size",
      min: 8,
      max: 128,
      step: 2,
    });
    atlas.addBinding(settings, "asciiChars", { label: "Characters" });

    const presentation = pane.addFolder({ title: "Presentation", expanded: false });
    presentation.addBinding(settings, "background", { label: "Background", view: "color" });
    pane.on("change", () => onChange({ ...settings }));

    const driftpane = createDriftpane(pane, {
      storageNamespace: "keyboard",
      width: 300,
      presetFolderTitle: "Driftpane",
    });

    const apply = (preset) => {
      Object.assign(settings, preset);
      pane.refresh();
      onChange({ ...settings });
    };
    onReady(() => apply);

    return () => {
      onReady(null);
      driftpane.dispose();
      pane.dispose();
    };
  }, [onChange, onReady]);

  return null;
};

const PresetBar = ({ applyPreset }) => (
  <nav className="preset-bar" aria-label="Visual presets">
    {Object.entries(PRESETS).map(([name, preset]) => (
      <button key={name} className="preset-pill" onClick={() => applyPreset?.(preset)}>
        {name}
      </button>
    ))}
  </nav>
);

const Loader = () => (
  <div className="loader" role="status" aria-live="polite">
    <span>Loading type</span>
  </div>
);

const App = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [applyPreset, setApplyPreset] = useState(null);
  const [fontReady, setFontReady] = useState(false);

  useEffect(() => {
    let active = true;
    const fonts = document.fonts;
    const waitForFont = fonts
      ? Promise.all([fonts.load('400 20px "Azeret Mono"'), fonts.ready])
      : Promise.resolve();

    waitForFont.finally(() => {
      if (active) setFontReady(true);
    });

    return () => { active = false; };
  }, []);

  return (
    <>
      {!fontReady && <Loader />}
      {fontReady && (
        <>
          <Controls onChange={setSettings} onReady={setApplyPreset} />
          <Scene settings={settings} />
          <PresetBar applyPreset={applyPreset} />
        </>
      )}
    </>
  );
};

export default App;
